// SP-2b — buyer autopay management. One panel, four states driven by the
// per-loan autopay status:
//
//   1. No bank mandate → plain-language explainer + Stripe bank-connect
//      (a us_bank_account SetupIntent on the loan's TPS connected account —
//      the staff AchMandateCard mechanics; connecting charges nothing).
//   2. Mandate on file, not enrolled → the consent screen: everything the
//      buyer agrees to on ONE screen (bank last4, the promise in three
//      short bullets, the full disclosure text, an explicit checkbox)
//      before "Turn on autopay".
//   3. Enrolled + Active → what will happen next + turn it off (with an
//      immediate in-place confirmation, no browser confirm()).
//   4. Suspended → why it's paused + the consent screen again to re-enroll.
//
// Portal rules: no React Query — useEffect+useState read with a cancelled
// guard; writes via useSubmittingMutation.

import { useCallback, useEffect, useState } from "react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  Button,
  Card,
  CardContent,
  Skeleton,
  StatusBadge,
  SubmitButton,
  useSubmittingMutation,
  useToast,
} from "@/design-system/components/ui";
import { formatCalendarDate, formatCurrency } from "@/design-system/lib/format";
import {
  portalBuyerFinanceApi,
  type BuyerAutopayMandateBegin,
  type BuyerAutopayStatus,
} from "../api/borrowerFinance";
import { stripeFor } from "../lib/stripeLoader";

export interface AutopayPanelProps {
  loanId: string;
  /** Notify the parent after enrollment/cancel/mandate changes so it can
   *  refresh its own view of the world (e.g. the Home checklist). */
  onChanged?: () => void | Promise<unknown>;
}

export function AutopayPanel({ loanId, onChanged }: AutopayPanelProps) {
  const [status, setStatus] = useState<BuyerAutopayStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const data = await portalBuyerFinanceApi.autopayStatus(loanId);
    setStatus(data);
    return data;
  }, [loanId]);

  useEffect(() => {
    let cancelled = false;
    setStatus(null);
    setLoadError(null);
    void (async () => {
      try {
        const data = await portalBuyerFinanceApi.autopayStatus(loanId);
        if (!cancelled) setStatus(data);
      } catch (err) {
        if (!cancelled)
          setLoadError(
            err instanceof Error ? err.message : "Couldn't load your autopay settings.",
          );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loanId]);

  const changed = useCallback(async () => {
    await reload();
    await onChanged?.();
  }, [reload, onChanged]);

  if (loadError) {
    return (
      <p className="text-small text-status-error" role="alert" data-testid="autopay-error">
        {loadError}
      </p>
    );
  }
  if (!status) {
    return <Skeleton className="h-24 w-full" data-testid="autopay-loading" />;
  }

  if (!status.mandateOnFile) {
    return <ConnectBankSection loanId={loanId} onConnected={changed} />;
  }

  if (status.enrolled && status.status === "Active") {
    return <EnrolledSection status={status} onChanged={changed} />;
  }

  // Suspended (warning + re-enroll) or simply not enrolled → consent screen.
  return (
    <div className="space-y-space-4" data-testid="autopay-consent-state">
      {status.status === "Suspended" && (
        <div
          className="rounded-lg border border-status-warning bg-white-warm p-space-3"
          data-testid="autopay-suspended-notice"
        >
          <StatusBadge tone="warn">Autopay paused</StatusBadge>
          <p className="mt-space-2 text-small text-onyx">
            {status.suspendReason ? `${status.suspendReason}. ` : ""}
            Autopay is paused — pay your current statement yourself, then re-enroll below.
          </p>
        </div>
      )}
      <ConsentSection status={status} onEnrolled={changed} />
    </div>
  );
}

// ── 1. Connect the bank account (Stripe SetupIntent) ───────────────────────

function ConnectBankSection({
  loanId,
  onConnected,
}: {
  loanId: string;
  onConnected: () => Promise<void>;
}) {
  const [intent, setIntent] = useState<BuyerAutopayMandateBegin | null>(null);
  const [beginError, setBeginError] = useState<string | null>(null);

  const begin = useSubmittingMutation(
    async () => {
      setBeginError(null);
      const data = await portalBuyerFinanceApi.beginAutopayMandate(loanId);
      setIntent(data);
    },
    {
      onError: (err) =>
        setBeginError(
          err instanceof Error
            ? err.message
            : "We couldn't start the bank connection. Please try again.",
        ),
    },
  );

  return (
    <div className="space-y-space-3" data-testid="autopay-connect-bank">
      <p className="text-small text-onyx">
        Connect the bank account your payments will come from. Connecting
        doesn&apos;t charge anything.
      </p>
      {beginError && (
        <p className="text-small text-status-error" role="alert">
          {beginError}
        </p>
      )}
      {!intent && (
        <Button
          variant="primary"
          size="sm"
          loading={begin.pending}
          disabled={begin.pending}
          onClick={() => void begin.submit()}
          data-testid="autopay-connect-bank-start"
        >
          Connect bank account
        </Button>
      )}
      {intent && (
        <Elements
          stripe={stripeFor(intent.publishableKey, intent.stripeAccount)}
          options={{ clientSecret: intent.clientSecret }}
        >
          <ConfirmMandateForm
            loanId={loanId}
            onConnected={onConnected}
            onCancel={() => setIntent(null)}
          />
        </Elements>
      )}
    </div>
  );
}

function ConfirmMandateForm({
  loanId,
  onConnected,
  onCancel,
}: {
  loanId: string;
  onConnected: () => Promise<void>;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  const confirm = useSubmittingMutation(
    async () => {
      if (!stripe || !elements) return;
      setError(null);
      // The SetupIntent's payment_method_types is us_bank_account, so the
      // Payment Element only offers the bank-account flow (no card tab).
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {},
        redirect: "if_required",
      });
      if (result.error) {
        setError(
          result.error.message ??
            "The bank account could not be saved. Please try again.",
        );
        return;
      }
      await portalBuyerFinanceApi.autopayMandateSync(loanId);
      await onConnected();
      toast.success("Bank account connected.");
    },
    {
      onError: (err) =>
        setError(
          err instanceof Error
            ? err.message
            : "The bank account could not be saved. Please try again.",
        ),
    },
  );

  return (
    <form
      className="flex flex-col gap-space-3"
      onSubmit={(e) => {
        e.preventDefault();
        void confirm.submit();
      }}
    >
      <PaymentElement />
      <p className="text-caption text-gray-mid m-0">
        We save your bank details securely with our payment provider. Nothing
        is charged now.
      </p>
      {error && (
        <p className="text-small text-status-error m-0" role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-space-2">
        <SubmitButton
          type="submit"
          variant="primary"
          size="sm"
          pending={confirm.pending}
          pendingLabel="Saving…"
          disabled={!stripe || !elements}
          data-testid="autopay-mandate-confirm"
        >
          Save bank account
        </SubmitButton>
        <Button
          type="button"
          variant="text"
          size="sm"
          onClick={onCancel}
          disabled={confirm.pending}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ── 2. The consent screen ───────────────────────────────────────────────────

function ConsentSection({
  status,
  onEnrolled,
}: {
  status: BuyerAutopayStatus;
  onEnrolled: () => Promise<void>;
}) {
  const toast = useToast();
  const [agreed, setAgreed] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const enroll = useSubmittingMutation(
    () => portalBuyerFinanceApi.enrollAutopay(status.loanId, status.consentVersion),
    {
      refresh: onEnrolled,
      onSuccess: () => toast.success("Autopay is on. We'll tell you before each draft."),
      onError: () => {
        // 400 = the consent text changed since it was shown; re-fetch (via
        // the parent's reload) and ask the buyer to review it again.
        setAgreed(false);
        setNotice("The authorization text was updated — please review it again.");
        void onEnrolled();
      },
    },
  );

  return (
    <div className="space-y-space-3" data-testid="autopay-consent">
      <p className="text-small text-onyx">
        Turn on autopay
        {status.bankLast4 && (
          <> from your bank account ending <span className="font-semibold">{status.bankLast4}</span></>
        )}
        . Here&apos;s what that means:
      </p>
      <ul className="list-disc space-y-space-1 pl-space-5 text-small text-onyx">
        <li>We&apos;ll draft the amount due on each monthly statement</li>
        <li>on its due date — we&apos;ll tell you the amount and date beforehand</li>
        <li>
          Turn it off here anytime — it stops immediately for anything not yet
          drafted
        </li>
      </ul>
      <div
        className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg border border-border-light bg-white-warm p-space-3 text-caption text-gray-mid"
        data-testid="autopay-disclosure"
      >
        {status.disclosureText}
      </div>
      {notice && (
        <p className="text-small text-status-error" role="alert" data-testid="autopay-consent-notice">
          {notice}
        </p>
      )}
      <label className="flex items-start gap-space-2 text-small text-onyx">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-[3px] h-4 w-4 accent-gold"
          data-testid="autopay-consent-checkbox"
        />
        <span>I authorize automatic bank drafts as described above</span>
      </label>
      <SubmitButton
        type="button"
        variant="primary"
        size="sm"
        pending={enroll.pending}
        pendingLabel="Turning on…"
        disabled={!agreed || enroll.pending}
        onClick={() => void enroll.submit()}
        data-testid="autopay-enroll"
      >
        Turn on autopay
      </SubmitButton>
    </div>
  );
}

// ── 3. Enrolled + Active ────────────────────────────────────────────────────

function EnrolledSection({
  status,
  onChanged,
}: {
  status: BuyerAutopayStatus;
  onChanged: () => Promise<void>;
}) {
  const toast = useToast();
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const cancel = useSubmittingMutation(
    () => portalBuyerFinanceApi.cancelAutopay(status.loanId),
    {
      refresh: onChanged,
      onSuccess: () =>
        toast.success(
          "Autopay is off. You're back to paying each statement yourself.",
        ),
      onError: (err) =>
        toast.error(
          err instanceof Error ? err.message : "Couldn't turn autopay off.",
        ),
    },
  );

  return (
    <Card data-testid="autopay-enrolled">
      <CardContent className="space-y-space-3">
        <div className="flex items-center gap-space-2">
          <StatusBadge tone="ok">Autopay is on</StatusBadge>
          {status.bankLast4 && (
            <span className="text-caption text-gray-mid">
              bank account ending {status.bankLast4}
            </span>
          )}
        </div>
        {status.nextDraft && (
          <p className="text-small text-onyx" data-testid="autopay-next-draft">
            We&apos;ll draft {formatCurrency(status.nextDraft.amount)} on{" "}
            {formatCalendarDate(status.nextDraft.draftDate)}.
          </p>
        )}
        {!confirmingCancel && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfirmingCancel(true)}
            data-testid="autopay-cancel"
          >
            Cancel autopay
          </Button>
        )}
        {confirmingCancel && (
          <div
            className="space-y-space-2 rounded-lg border border-border-light bg-white-warm p-space-3"
            data-testid="autopay-cancel-confirm"
          >
            <p className="text-small text-onyx">
              Turn off autopay? It stops immediately — nothing that hasn&apos;t
              been drafted yet will be drafted, and you&apos;ll pay each
              statement yourself.
            </p>
            <div className="flex flex-wrap items-center gap-space-2">
              <SubmitButton
                type="button"
                variant="primary"
                size="sm"
                pending={cancel.pending}
                pendingLabel="Turning off…"
                onClick={() => void cancel.submit()}
                data-testid="autopay-cancel-yes"
              >
                Yes, turn it off
              </SubmitButton>
              <Button
                variant="text"
                size="sm"
                onClick={() => setConfirmingCancel(false)}
                disabled={cancel.pending}
              >
                Keep autopay on
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
